import { supabase, FUNCTIONS_URL } from '../lib/supabase';

// Map weekly_check_ins snake_case → camelCase for components
const mapCheckIn = (c) => c ? ({
  id: c.id,
  weekNumber: c.week_number,
  weight: c.weight,
  waistMeasurement: c.waist_measurement,
  hipMeasurement: c.hip_measurement,
  chestMeasurement: c.chest_measurement,
  sleepHours: c.sleep_hours,
  waterIntake: c.water_intake,
  exerciseDone: c.exercise_done,
  challengesFaced: c.challenges_faced,
  goalsNextWeek: c.goals_next_week,
  overallMood: c.overall_mood,
  additionalNotes: c.additional_notes,
  medicationTaken: c.medication_taken,
  missedDoses: c.missed_doses,
  currentDosage: c.current_dosage,
  injectionSite: c.injection_site,
  bloodPressureSystolic: c.blood_pressure_systolic,
  bloodPressureDiastolic: c.blood_pressure_diastolic,
  bloodSugarFasting: c.blood_sugar_fasting,
  bloodSugarPostMeal: c.blood_sugar_post_meal,
  heartRate: c.heart_rate,
  sideEffects: c.side_effects,
  sideEffectsSeverity: c.side_effects_severity,
  sideEffectsNotes: c.side_effects_notes,
  appetiteLevel: c.appetite_level,
  energyLevel: c.energy_level,
  medicalConcerns: c.medical_concerns,
  submittedAt: c.submitted_at,
}) : null;

// Map plan_documents snake_case → camelCase for components
const mapDocument = (d) => ({
  id: d.id,
  planType: d.plan_type,
  documentType: d.document_type,
  fileName: d.file_name,
  storagePath: d.storage_path,
  fileSize: d.file_size,
  version: d.version,
  isActive: d.is_active,
  createdAt: d.created_at,
});

export const dashboardAPI = {
  // Dashboard Summary — assembled from multiple direct queries
  getDashboardSummary: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const [profileResult, planResult, subscriptionResult, checkInResult, checkInCountResult, documentsResult] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', user.id).single(),
      supabase.from('user_plans').select('*').eq('user_id', user.id).eq('status', 'ACTIVE').maybeSingle(),
      supabase.from('subscriptions').select('*').eq('user_id', user.id).eq('status', 'ACTIVE').maybeSingle(),
      supabase.from('weekly_check_ins').select('*').eq('user_id', user.id)
        .order('week_number', { ascending: false }).limit(1).maybeSingle(),
      supabase.from('weekly_check_ins').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
      supabase.from('plan_documents').select('*').eq('is_active', true)
        .or(`assigned_to.is.null,assigned_to.eq.${user.id}`)
        .order('created_at', { ascending: false }),
    ]);

    const activePlan = planResult.data;
    const latestCheckIn = mapCheckIn(checkInResult.data);
    const subscription = subscriptionResult.data;

    // Derive current week from last submitted check-in
    const lastWeek = checkInResult.data?.week_number ?? 0;
    const currentWeek = lastWeek + 1;

    // Map subscription snake_case → camelCase for planType detection
    const mappedSubscription = subscription ? {
      ...subscription,
      planId: subscription.plan_id,
      planName: subscription.plan_name,
      startDate: subscription.start_date,
      endDate: subscription.end_date,
      isActive: subscription.status === 'ACTIVE',
      daysRemaining: Math.max(0, Math.ceil((new Date(subscription.end_date) - new Date()) / (1000 * 60 * 60 * 24))),
    } : null;

    return {
      profile: profileResult.data,
      activePlan,
      subscription: mappedSubscription,
      latestCheckIn,
      currentWeek,
      totalCheckIns: checkInCountResult.count ?? 0,
      hasActiveSubscription: !!subscription,
      availableDocuments: (documentsResult.data ?? []).map(mapDocument),
    };
  },

  // Weekly Check-ins — upsert so re-submitting same week updates instead of duplicating
  submitCheckIn: async (data) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Check if check-in for this week already exists
    const { data: existing } = await supabase
      .from('weekly_check_ins')
      .select('id')
      .eq('user_id', user.id)
      .eq('week_number', data.week_number)
      .maybeSingle();

    let result, error;
    if (existing) {
      // Update existing
      ({ data: result, error } = await supabase
        .from('weekly_check_ins')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', existing.id)
        .select().single());
    } else {
      // Insert new
      ({ data: result, error } = await supabase
        .from('weekly_check_ins')
        .insert({ ...data, user_id: user.id })
        .select().single());
    }
    if (error) throw error;
    return mapCheckIn(result);
  },

  getCheckInHistory: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('weekly_check_ins').select('*').eq('user_id', user.id)
      .order('week_number', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapCheckIn);
  },

  getLatestCheckIn: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('weekly_check_ins').select('*').eq('user_id', user.id)
      .order('week_number', { ascending: false }).limit(1).single();
    if (error && error.code !== 'PGRST116') throw error;
    return data ? mapCheckIn(data) : null;
  },

  getCheckInByWeek: async (weekNumber) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('weekly_check_ins').select('*').eq('user_id', user.id)
      .eq('week_number', weekNumber).single();
    if (error && error.code !== 'PGRST116') throw error;
    return data ? mapCheckIn(data) : null;
  },

  // Plan Documents — from Supabase Storage
  getAvailableDocuments: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('plan_documents')
      .select('*')
      .eq('is_active', true)
      .or(`assigned_to.is.null,assigned_to.eq.${user.id}`)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapDocument);
  },

  // Download plan document — get signed URL from Supabase Storage
  downloadDocument: async (documentId) => {
    const { data: doc, error: docErr } = await supabase
      .from('plan_documents').select('storage_path, file_name').eq('id', documentId).single();
    if (docErr) throw docErr;

    const { data, error } = await supabase.storage
      .from('plan-documents')
      .createSignedUrl(doc.storage_path, 60); // 60 seconds expiry
    if (error) throw error;
    return { url: data.signedUrl, fileName: doc.file_name };
  },

  getDocumentDownloadUrl: async (documentId) => {
    const result = await dashboardAPI.downloadDocument(documentId);
    return result.url;
  },

  // Generate & download plan PDF via edge function
  downloadPlanPdf: async (planId) => {
    // Use supabase.functions.invoke for correct auth headers; responseType blob
    const { data, error } = await supabase.functions.invoke(
      `generate-plan-pdf?planId=${planId}`,
      { method: 'GET' }
    );
    if (error) throw new Error(error.message || 'Failed to generate PDF');
    // data is already parsed — if it's a URL string, return as-is; else assume blob
    return data;
  },

  // Note: Weekly Photos feature is removed (deferred to future version)
  uploadPhoto: async () => { throw new Error('Photo upload not available in this version'); },
  getUserPhotos: async () => [],
  getPhotosByWeek: async () => [],
  deletePhoto: async () => {},
  getPhotoDownloadUrl: () => null,
};

export default dashboardAPI;
